<template>
  <div>
    <div
      class="w-full bg-white"
      :style="{
        filter: `brightness(${imageBrightnessFilter}%)`,
        backgroundColor: imageBackgroundColor?.hexa || 'transparent',
      }"
    >
      <UiLazyImg
        class="aspect-[167/229]"
        :src="productImages?.[0]?.url || '/images/no-image.webp'"
        :alt="product.currentTranslation?.title"
      />
    </div>
    <div class="relative px-2 pb-2 pt-1.5">
      <div class="flex gap-2">
        <NuxtLink
          v-if="productTitle"
          :to="productLink"
          class="block text-xs font-medium uppercase leading-tight"
          @click="onTitleClick"
        >
          <span>{{ productTitle }}</span>
        </NuxtLink>
        <UiButton
          v-if="product.default_sku"
          class="ml-auto size-5 shrink-0 rounded-full bg-transparent text-primary"
          variant="clear"
          variant-type="icon"
          text="xs"
          :aria-label="$t('addToWishlist')"
          @click="updateWishlist"
        >
          <ClientOnly>
            <UiIcon
              :name="inWishlist ? 'heart-bold' : 'heart'"
              class="size-3"
              :class="[inWishlist ? 'fill-primary text-primary' : '']"
              aria-hidden="true"
            />
          </ClientOnly>
        </UiButton>
      </div>
      <p class="mt-1 flex items-center gap-1 text-xs font-normal text-primary">
        <span
          v-if="isConfigurationOutOfStockPlp"
          class="text-black-600 lowercase"
        >
          {{ $t("outOfStock") }}
        </span>
        <span v-else class="text-xs font-medium text-primary">
          {{ $n(productPrice, "currency") }}
        </span>
      </p>
      <ProductMaterialSelectView
        v-if="ringSizeMaterialCategory"
        class="materials-select"
        hide-label
        :block="ringSizeMaterialCategory"
        :title="ringSizeMaterialCategory?.currentTranslation?.title"
        :selected-item="ringSizeSelectedItem"
        @filter="handleFilter"
      />
      <UiButton
        v-if="productSKU && !isConfigurationOutOfStockPlp"
        class="font-akzidenz-grotesk-pro mt-1.5 w-full py-1.5 text-[10px] font-normal"
        :loading="addToCartPending"
        :disabled="addToCartPending"
        data-e2e-btn="add-to-cart"
        @click="onAddToCart(true)"
      >
        {{ $t("addToCart") }}
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  CatalogData,
  ColorPickerData,
  FilteredMaterialsByCategoriesI,
  IProductMaterialFilterEvent,
  MaterialData,
} from "ecom-types";

const props = withDefaults(
  defineProps<{
    imageBrightnessFilter?: string;
    product: CatalogData;
    productIndex: number;
    statisticItemListName?: string;
    statisticItemListId?: string;
    imageBackgroundColor?: ColorPickerData;
    productImages: any[];
    productTitle: string;
    discountValue: number;
    isConfigurationInventoryOutOfStockPlp: boolean;
    isConfigurationOutOfStockPlp: boolean;
    productPrice: number;
    productLink: string;
    ringSizeMaterialCategory: FilteredMaterialsByCategoriesI | null;
    ringSizeSelectedItem: MaterialData | null;
    showOutOfStock?: boolean;
    productSKU: string;
    addToCartPending: boolean;
    inWishlist: boolean;
    setGaSelectItemEvent: Function;
    handleFilter: (value: IProductMaterialFilterEvent) => void;
    updateWishlist: Function;
    onAddToCart: Function;
  }>(),
  {
    showOutOfStock: false,
  },
);

const { setItemListContext } = useItemListContext();

function onTitleClick() {
  setItemListContext(
    props.statisticItemListName || "",
    props.statisticItemListId || "",
  );
  props.setGaSelectItemEvent && props.setGaSelectItemEvent();
}
</script>

<style scoped>
.materials-select {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

:deep(.materials-select) select {
  font-size: 0.875rem;
  line-height: 1.25rem;
  height: fit-content;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

:deep(.materials-select) .text-cherry {
  display: none;
}

:deep(.materials-select) label {
  display: none;
}
</style>

